import { Flex, Skeleton } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { StoresInventoryStatus } from '../../../../bff-api/stores/model';
import { isAvailableStatus } from '../../../../bff-api/utils/store-utils';
import { usePDP } from '../../../../contexts/pdp/usePDP';
import { StorageKey } from '../../../../hooks/model/StorageKey';
import { useLocalStorage } from '../../../../hooks/useLocalStorage';
import { useSameDayDelivery } from '../../../../hooks/useSameDayDelivery';
import { Text } from '../../../../ui-components';
import type { OrderAddress } from '../../../../bff-api/baskets/model';

const { PRODUCT_ONLY_AVAILABLE_IN_ANOTHER_VARIANT } = StoresInventoryStatus;

interface Props {
    id: string;
    onClickLocation: () => void;
}

export const SameDayDeliveryBox: React.FC<Props> = ({ id, onClickLocation }: Props) => {
    const {
        isLoadingSDDInventory,
        inventoryStore: {
            currentInventory: { status },
        },
    } = usePDP();
    const address = useLocalStorage<OrderAddress | null>(StorageKey.SDD_ADDRESS).data;
    const { isNextDayDelivery } = useSameDayDelivery();

    const isAvailable = address && isAvailableStatus(status);

    const isAvailableInAnotherVariant =
        address && !isAvailable && status === PRODUCT_ONLY_AVAILABLE_IN_ANOTHER_VARIANT;

    const isNotAvailable = address && !isAvailable && !isAvailableInAnotherVariant;

    return (
        <Flex
            data-testid={id}
            direction="column"
            gap={2}
            padding={3}
            borderRadius={4}
            background="slt-grey.100"
            width="100%"
        >
            {isLoadingSDDInventory ? (
                <Skeleton data-testid={`${id}-loading`} w="100%" h="100%" />
            ) : (
                <Flex data-testid={`${id}-location`} direction="column" gap={1}>
                    {!address ? (
                        <>
                            <Text id={`${id}-location-order-now`} style="bold">
                                <FormattedMessage
                                    id="sdd.order.now.get.today"
                                    defaultMessage="Order now, get it today"
                                />
                            </Text>

                            <Text
                                id={`${id}-location-button`}
                                type="link"
                                onClick={onClickLocation}
                            >
                                <FormattedMessage
                                    id="sdd.enter.your.location"
                                    defaultMessage="Enter your Location"
                                />
                            </Text>
                        </>
                    ) : null}

                    {isAvailable ? (
                        <>
                            <Text id={`${id}-location-order-now`} style="bold">
                                {isNextDayDelivery ? (
                                    <FormattedMessage
                                        id="sdd.order.now.get.tomorrow"
                                        defaultMessage="Order now, get it tomorrow"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="sdd.order.now.get.today"
                                        defaultMessage="Order now, get it today"
                                    />
                                )}
                            </Text>

                            <Flex gap={1}>
                                <Text id={`${id}-location-deliver-to`} style="bold">
                                    <FormattedMessage
                                        id="sdd.deliver.to"
                                        defaultMessage="Deliver to"
                                    />
                                </Text>

                                <Text
                                    id={`${id}-location-button`}
                                    type="link"
                                    onClick={onClickLocation}
                                >
                                    {address.postalCode}
                                </Text>
                            </Flex>
                        </>
                    ) : null}

                    {isAvailableInAnotherVariant ? (
                        <>
                            <Flex gap={1}>
                                <Text id={`${id}-location-not-available-for`} style="bold">
                                    <FormattedMessage
                                        id="sdd.not.available"
                                        defaultMessage="Item not available for"
                                    />
                                </Text>

                                <Text
                                    id={`${id}-location-button`}
                                    type="link"
                                    onClick={onClickLocation}
                                >
                                    {address?.postalCode}
                                </Text>
                            </Flex>

                            <Text id={`${id}-location-select-different-variant`} size="small">
                                <FormattedMessage
                                    id="sdd.enter.different.color.size.location"
                                    defaultMessage="Please select a different color/size or Location."
                                />
                            </Text>
                        </>
                    ) : null}

                    {isNotAvailable ? (
                        <>
                            <Flex gap={1}>
                                <Text id={`${id}-location-not-available-for`} style="bold">
                                    <FormattedMessage
                                        id="sdd.not.available"
                                        defaultMessage="Item not available for"
                                    />
                                </Text>

                                <Text
                                    id={`${id}-location-button`}
                                    type="link"
                                    onClick={onClickLocation}
                                >
                                    {address.postalCode}
                                </Text>
                            </Flex>

                            <Text id={`${id}-location-select-different-location`} size="small">
                                <FormattedMessage
                                    id="sdd.enter.different.location"
                                    defaultMessage="Please enter a different location."
                                />
                            </Text>
                        </>
                    ) : null}
                </Flex>
            )}
        </Flex>
    );
};
